import styled from "@emotion/styled";
import { MouseEvent, useState } from "react";
import { Button, buttonClasses, Checkbox, Menu, MenuItem } from "@kaltura/ds-react-components";
import { ChevronDown16Icon, ChevronUp16Icon } from "@kaltura/ds-react-icons";
import { translate } from "@kaltura/mediaspace-shared-utils";

export interface FilterProps {
    className?: string;
    param: string;
    label: string;
    options: string[];
    countSelected?: boolean;
    values?: string[];
    allValuesOption?: string;
    onChange: (param: string, values: string[]) => void;
}

const StyledButton = styled(Button)({
    [`.${buttonClasses.label}`]: {
        justifyContent: "flex-start",
    },
});

const StyledButtonLabel = styled("span")({
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    width: "100%",
    textAlign: "left",
});

const StyledMenuItemLabel = styled("span")(({ theme }) => ({
    marginLeft: theme.spacing(1),
}));

/**
 * Session single Filter
 */
export function Filter(props: FilterProps) {
    const { className, param, label, options, countSelected = false, values = [], allValuesOption, onChange } = props;

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleSelection = (value: string) => {
        // current value status
        const valueSelected = values && values.includes(value);
        // add or remove the value from the selected filter values
        const currentValues =
            values.length > 0
                ? // we have values
                  valueSelected
                    ? // remove value from selected values
                      values.filter((val: string) => val !== value)
                    : // add value to selected values
                      values.concat(value)
                : // no selected values - just add this one
                  [value];

        onChange(param, currentValues);
    };

    const handleSelectAll = () => {
        // `select all` equals no filters
        onChange(param, []);
    };

    return (
        <div className={className}>
            <StyledButton
                variant="select"
                fullWidth={true}
                id={`AgendaMenuToggleBtn${param}`}
                aria-haspopup={true}
                aria-expanded={open ? "true" : undefined}
                onClick={handleButtonClick}
                aria-controls={open ? `AgendaMenu${param}` : undefined}
                endIcon={open ? <ChevronUp16Icon /> : <ChevronDown16Icon />}
            >
                <StyledButtonLabel>
                    {values.length > 0 && countSelected
                        ? // Intentionally use translate() and not translatePlural()
                          // because we have only one form in the module's configs
                          translate(`%1 ${label}`, [values.length])
                        : label}
                </StyledButtonLabel>
            </StyledButton>

            <Menu
                id={`AgendaMenu${param}`}
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                aria-labelledby={`AgendaMenuToggleBtn${param}`}
                className="kms-ds-agenda-filter-menu"
                PaperProps={{
                    style: {
                        maxHeight: "320px",
                        overflowY: "auto",
                    },
                }}
            >
                {allValuesOption && (
                    <MenuItem
                        className="kms-ds-agenda-filter-menu-item"
                        key={"selectall"}
                        onClick={() => handleSelectAll()}
                    >
                        <Checkbox checked={values.length === 0} />
                        <StyledMenuItemLabel className="kms-ds-agenda-filter-menu-item-label">
                            {allValuesOption}
                        </StyledMenuItemLabel>
                    </MenuItem>
                )}

                {options.map((option: string) => (
                    <MenuItem key={option} onClick={() => handleSelection(option.trim())}>
                        <Checkbox checked={values.includes(option.trim())} />
                        <StyledMenuItemLabel className="kms-ds-agenda-filter-menu-item-label">
                            {option.trim()}
                        </StyledMenuItemLabel>
                    </MenuItem>
                ))}
            </Menu>
        </div>
    );
}

export default Filter;
